/**
 * Responsive tier detection for the cinematic scene.
 *
 * Three tiers, deliberately not a single width threshold:
 * - `desktop` — fine pointer and enough room for a spread plus camera work.
 * - `tablet`  — touch input, spread in landscape, single page in portrait.
 * - `phone`   — touch input, always a single page, no camera zoom or drift.
 */

const QUERIES = {
  wide: '(min-width: 1280px)',
  medium: '(min-width: 768px)',
  finePointer: '(hover: hover) and (pointer: fine)',
  landscape: '(orientation: landscape)',
  // A phone on its side: wide enough for a spread, but only ~400px tall, so
  // the stage and the engine's own floors have to be relaxed to fit one.
  shortLandscape: '(orientation: landscape) and (max-height: 560px)'
};

function readMatches() {
  const result = {};
  for (const [name, query] of Object.entries(QUERIES)) {
    result[name] = window.matchMedia(query).matches;
  }
  return result;
}

function resolveTier(matches) {
  if (!matches.medium) return 'phone';
  if (matches.wide && matches.finePointer) return 'desktop';
  return 'tablet';
}

/**
 * @returns {'desktop'|'tablet'|'phone'}
 */
export function getTier() {
  return resolveTier(readMatches());
}

export function hasFinePointer() {
  return window.matchMedia(QUERIES.finePointer).matches;
}

export function isLandscape() {
  return window.matchMedia(QUERIES.landscape).matches;
}

export function isShortLandscape() {
  return window.matchMedia(QUERIES.shortLandscape).matches;
}

/**
 * Watch for tier changes. Fires only when the resolved tier actually differs,
 * so consumers never remount the book on an irrelevant resize.
 *
 * @param {(tier: 'desktop'|'tablet'|'phone') => void} onChange
 * @returns {() => void} unsubscribe
 */
export function watchTier(onChange) {
  const lists = Object.values(QUERIES).map((query) => window.matchMedia(query));
  let current = getTier();

  function handleChange() {
    const next = getTier();
    if (next === current) return;
    current = next;
    onChange(next);
  }

  for (const list of lists) list.addEventListener('change', handleChange);

  return () => {
    for (const list of lists) list.removeEventListener('change', handleChange);
  };
}
